import { getOidcRedirectOrigin, getOidcRedirectUri } from '../../utils/config';

const OIDC_MESSAGE_TYPE = 'NICE_OIDC_CALLBACK';
const POPUP_CLOSED_GRACE_MS = 3000;

export { getOidcRedirectUri };

function getAllowedCallbackOrigins(): Set<string> {
  return new Set([getOidcRedirectOrigin(), window.location.origin.replace(/\/+$/, '')]);
}

export function waitForOidcCallback(popup: Window, expectedState: string): Promise<string> {
  const allowedOrigins = getAllowedCallbackOrigins();

  return new Promise((resolve, reject) => {
    let settled = false;
    let popupClosedAt: number | null = null;

    const timeoutMs = 5 * 60 * 1000;
    const timeoutId = window.setTimeout(() => {
      if (settled) {
        return;
      }
      cleanup();
      reject(new Error('NICE sign-in timed out. Try again.'));
    }, timeoutMs);

    const intervalId = window.setInterval(() => {
      if (settled) {
        return;
      }

      if (popup.closed) {
        if (popupClosedAt === null) {
          popupClosedAt = Date.now();
          return;
        }

        if (Date.now() - popupClosedAt >= POPUP_CLOSED_GRACE_MS) {
          cleanup();
          reject(new Error('NICE sign-in was cancelled.'));
        }
        return;
      }

      popupClosedAt = null;
    }, 200);

    const onMessage = (event: MessageEvent) => {
      if (settled || !allowedOrigins.has(event.origin)) {
        return;
      }

      if (!event.data || event.data.type !== OIDC_MESSAGE_TYPE) {
        return;
      }

      if (event.data.state && event.data.state !== expectedState) {
        settled = true;
        cleanup();
        reject(new Error('NICE sign-in state mismatch. Try again.'));
        return;
      }

      if (event.data.error) {
        settled = true;
        cleanup();
        reject(new Error(event.data.errorDescription || event.data.error));
        return;
      }

      if (!event.data.code) {
        settled = true;
        cleanup();
        reject(new Error('NICE sign-in did not return an authorization code.'));
        return;
      }

      settled = true;
      cleanup();
      resolve(String(event.data.code));
    };

    const cleanup = () => {
      window.clearTimeout(timeoutId);
      window.clearInterval(intervalId);
      window.removeEventListener('message', onMessage);
      if (!popup.closed) {
        popup.close();
      }
    };

    window.addEventListener('message', onMessage);
  });
}
